import { FormikProvider } from 'formik'

import { Alert, Form, Loader, Text } from '@/ui'
import { PageHeader } from '@/components/PageHeader'
import { FormFlowFooter } from '@/components/form-flow/FormFlowFooter'
import { TypographyStyles } from '@/styles'
import { US_STATES_WITH_NAMES } from '@/constants/locationConstants'
import {
  ANNUAL_TAXES_PATH,
  useContractorFilingQuestionnaire,
} from './useContractorFilingQuestionnaire'

const BUSINESS_NAME_DESCRIPTION =
  'This must exactly match the business name on your IRS EIN documentation. Even small differences (spacing, punctuation, or abbreviations) can cause your 1099-NEC filing to be rejected and delay processing.'
const STATES_DESCRIPTION =
  'Select the States where the contractor actually performed the work, not where you or your business are located.'

const STATE_OPTIONS = US_STATES_WITH_NAMES.map(({ code, name }) => ({
  value: code,
  text: name,
  searchText: code,
}))

const ContractorFilingQuestionnairePage = () => {
  const {
    isAwaitingAnswers,
    isPrefillLoading,
    prefill,
    submitError,
    formik,
    submit,
    backToAnnualTaxes,
  } = useContractorFilingQuestionnaire()

  if (!isAwaitingAnswers || isPrefillLoading) {
    return <Loader role="status" aria-label="Loading" />
  }

  return (
    <div className="mx-auto max-w-2xl px-4 pb-24">
      <PageHeader
        header="1099-NEC filing"
        backControl={{ link: ANNUAL_TAXES_PATH, text: 'Back to Annual Taxes' }}
      />
      <Text variant={TypographyStyles.BODY_MD} className="my-4">
        Answer these questions so a Taxfyle preparer can file your 1099-NECs.
      </Text>
      {submitError && (
        <Alert variant="error" className="mb-4">
          {submitError.message}
        </Alert>
      )}
      <FormikProvider value={formik}>
        <div className="space-y-6">
          <Form.Input
            name="businessName"
            label="Please enter the name of your business"
            description={
              prefill?.businessNameIsLegalName
                ? `We've filled in your own name, which is usually right for a sole proprietor. ${BUSINESS_NAME_DESCRIPTION}`
                : BUSINESS_NAME_DESCRIPTION
            }
          />
          <Form.BooleanRadioGroup
            name="hasPayrollProvider"
            label="Have you engaged a payroll provider for services either now or in the past?"
            description="This helps determine whether another provider (such as a payroll provider like Gusto) may have already filed tax forms on your behalf."
          />
          <Form.Input
            name="contractorCount"
            inputMode="numeric"
            placeholder="Number of 1099s"
            label="Please indicate approximately how many 1099s need to be prepared?"
            description="Indicate the number of independent contractors that require a 1099-NEC supplied to them."
          />
          <Form.Multiselect
            name="contractorStates"
            label="Indicate the States where the contractor performed the work"
            description={
              prefill?.contractorStates?.length
                ? "We've started with your practice's state. Change it if your contractors worked somewhere else."
                : STATES_DESCRIPTION
            }
            options={STATE_OPTIONS}
            searchable
          />
        </div>
      </FormikProvider>
      <FormFlowFooter
        onBack={backToAnnualTaxes}
        onForward={submit}
        isSubmit
        loading={formik.isSubmitting}
        continueDisabled={formik.isSubmitting}
        continueButtonText="Submit"
      />
    </div>
  )
}

export default ContractorFilingQuestionnairePage
